import debug from "debug";

const log = debug("app:image:imagevault");



/**
 * Manages the caching and retrieval of images using both in-memory storage and IndexedDB for persistence.
 * @category Experience
 * @subcategory Utils
 */
class ImageVault {
  static instance;

  /**
   * Creates an instance of ImageVault or returns an existing instance.
   * @param {Object} experience - The experience object containing configuration and event hub.
   */
  constructor(experience) {
    if (ImageVault.instance) {
      return ImageVault.instance;
    }
    ImageVault.instance = this;

    this.experience = experience;
    this.eventHub = experience.eventHub;
    this.images = new Map(); // In-memory cache for quick access within a session

    this.initialize();
  }

  cleanup() {
    console.log("Cleaning up ImageVault: Removing event listeners and clearing image cache.");
  
    this.eventHub.off("getImages", this.handleImageRequests);
    
    this.images.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
    this.images.clear();
  
    ImageVault.instance = null;
    console.log("ImageVault cleanup complete.");
  }

  /**
   * Returns the singleton instance of ImageVault.
   * @param {Object} experience - The experience object containing configuration and event hub.
   * @returns {ImageVault} The singleton instance of ImageVault.
   */
  static getInstance(experience) {
    if (!ImageVault.instance) {
      ImageVault.instance = new ImageVault(experience);
    }
    return ImageVault.instance;
  }

  /**
   * Initializes the ImageVault by setting up event listeners.
   */
  initialize() {
    this.eventHub.on("getImages", (imageUrls) => {
      this.handleImageRequests(imageUrls);
    });
  }

  /**
   * Handles image requests by processing a single URL or an array of URLs.
   * @param {string|string[]} imageUrls - A single image URL or an array of image URLs.
   */
  async handleImageRequests(imageUrls) {
    if (Array.isArray(imageUrls)) {
      for (const url of imageUrls) {
        await this.addRequest(url);
      }
    } else if (typeof imageUrls === "string") {
      await this.addRequest(imageUrls);
    } else {
      console.error("Invalid imageUrls type. Expected string or array.");
    }
  }

  /**
   * Adds an image request to the vault and fetches the image if not already cached.
   * @param {string} key - The key or URL of the image to request.
   */
  async addRequest(key) {
    if (this.images.has(key)) {
      this.notifyRequestor(key, this.images.get(key));
    } else {
      const blobUrl = await this.fetchAndCacheImage(key);
      if (blobUrl) {
        this.images.set(key, blobUrl); // Cache in memory for quick access within session
        this.notifyRequestor(key, blobUrl);
      }
    }
  }

  /**
   * Fetches and caches an image, storing it in memory and IndexedDB.
   * @param {string} key - The key or URL of the image to fetch.
   * @returns {Promise<string|null>} A promise resolving to the blob URL of the image, or null if not found.
   */
  async fetchAndCacheImage(key) {
    const fullUrl = `${this.experience.config.assetURL}${key}`;
    const cacheExtensions = [".svg", ".basis", ".ktx2"];
    const shouldCache = cacheExtensions.some(ext => key.endsWith(ext));
  
    const db = await this.openDatabase();
    let cachedImage = shouldCache ? await this.getCachedImage(db, fullUrl) : null;
  
    if (!cachedImage) {
      try {
        const response = await fetch(fullUrl);
        if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
  
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
  
        if (shouldCache) {
          this.images.set(key, blobUrl); // Cache in memory
          await this.cacheImage(db, fullUrl, blob); // Store actual image blob
        }
  
        cachedImage = blobUrl;
      } catch (error) {
        console.error(`Error fetching image ${key}: ${error.message}`);
      }
    }
  
    return cachedImage;
  }

  /**
   * Fetches an image from a URL.
   * @param {string} url - The URL of the image to fetch.
   * @returns {Promise<string>} A promise resolving to the blob URL of the image.
   * @throws {Error} If the image fetch fails.
   */
  async fetchImage(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch image");
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error(`Error fetching image from ${url}: ${error.message}`);
      throw error;
    }
  }

  /**
   * Notifies the requestor that an image is ready.
   * @param {string} key - The key or URL of the image.
   * @param {string} blobUrl - The blob URL of the image.
   */
  notifyRequestor(key, blobUrl) {
    this.eventHub.emit("imageReady", { key, blobUrl });
  }

  /**
   * Opens an IndexedDB database for caching images.
   * @returns {Promise<IDBDatabase>} A promise resolving to the opened IndexedDB instance.
   */
  openDatabase() {
    return new Promise((resolve, reject) => {
      const dbVersion = 5; // Use the latest version
      const request = indexedDB.open("ImageCacheDB", dbVersion);
  
      request.onerror = () => reject(request.error);
  
      request.onsuccess = () => resolve(request.result);
  
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        const store = db.objectStoreNames.contains("images")
          ? event.target.transaction.objectStore("images")
          : db.createObjectStore("images", { keyPath: "url" });
  
        // Ensure `blob` field exists
        if (!store.indexNames.contains("blob")) {
          store.createIndex("blob", "blob");
        }
      };
    });
  }

  /**
   * Retrieves a cached image from IndexedDB.
   * @param {IDBDatabase} db - The IndexedDB instance.
   * @param {string} url - The URL of the image to retrieve.
   * @returns {Promise<string|null>} A promise resolving to the blob URL of the cached image, or null if not found.
   */
  getCachedImage(db, url) {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction("images", "readonly");
      const store = transaction.objectStore("images");
      const request = store.get(url);
  
      request.onsuccess = () => {
        if (request.result) {
          const blobUrl = URL.createObjectURL(request.result.blob);
          resolve(blobUrl);
        } else {
          resolve(null);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }
  


  /**
 * Caches an image in IndexedDB by storing the actual image blob.
 * @param {IDBDatabase} db - The IndexedDB instance.
 * @param {string} url - The URL of the image to cache.
 * @param {Blob} blob - The actual image blob.
 * @returns {Promise<void>} A promise that resolves when the image is cached.
 */
async cacheImage(db, url, blob) {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("images", "readwrite");
    const store = transaction.objectStore("images");

    store.put({ url, blob });

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

  /**
   * Retrieves a blob URL for a cached image or fetches it if not cached.
   * @param {string} url - The URL of the image to retrieve.
   * @returns {Promise<string|null>} A promise resolving to the blob URL of the image or null if not found.
   */
  async getBlobImage(url) {
    if (this.images.has(url)) {
      return this.images.get(url);
    } else {
      const db = await this.openDatabase();
      const cachedImage = await this.getCachedImage(db, url);
      return cachedImage || "No Image";
    }
  }
}

export default ImageVault;
